import { useMemo } from 'react';
import Flex from '@creditflowresearch/uilib/Flex';
import Grid from '@creditflowresearch/uilib/Grid';
import GridItem from '@creditflowresearch/uilib/GridItem';
import CollapsibleSectionList from '@creditflowresearch/uilib/CollapsibleSectionList';
import { ISection } from '@creditflowresearch/uilib/Section';
import useResponsive, { QUERIES } from '@creditflowresearch/uilib/hooks/useResponsive';
import SectionService from '@/creditflowresearch/domain/Header/Section';
import { resolveExpandedKeys, ExpandStrategy } from '@/creditflowresearch/domain/Header/Section/expandStrategy';

import styles from './sectionLayout.module.scss';

interface ISectionLayoutDesktopProps {
  title?: string,
  sectionsGrid: ISection[][][],
  expandStrategy?: ExpandStrategy,
}

const getSectionIds = (sections: ISection[]): string => {
  return sections.map((section) => section.id).join('-');
};

const getRowKey = (row: ISection[][]): string => {
  return row.map((column) => getSectionIds(column)).join('-');
};

const SectionLayoutDesktop = ({
  title,
  sectionsGrid,
  expandStrategy = { type: 'none' },
}: ISectionLayoutDesktopProps) => {
  const isTablet = useResponsive(QUERIES.TABLET);

  const rows = useMemo(() => {
    if (!sectionsGrid?.length) {
      return [];
    }

    return isTablet ? sectionsGrid : SectionService.combineRowsForDesktop(sectionsGrid);
  }, [sectionsGrid, isTablet]);

  const globalExpandedKeys = useMemo(() => {
    const allSections = rows.flat(2);
    return resolveExpandedKeys(allSections, expandStrategy);
  }, [rows, expandStrategy]);

  if (!rows.length) {
    return null;
  }

  return (
    <Flex direction="column" gap={ { sm: 20, md: 10, xl: 10 } }>
      { title && (
        <Flex
          as="h2"
          className={ styles.title }
        >
          { title }
        </Flex>
      ) }

      { rows.map((row) => {
        const rowKey = getRowKey(row);
        return (
          <Grid
            key={ `row-${ rowKey }` }
            templateColumns={ `repeat(${ row.length }, 1fr)` }
            gap={ { sm: 20, md: 0, xl: 0 } }
          >
            { row.map((column) => {
              const columnExpandedKeys = globalExpandedKeys.filter(
                (key) => column.some((section) => section.id === key),
              );

              return (
                <GridItem
                  key={ `column-${ rowKey }-${ getSectionIds(column) }` }
                  colSpan={ 1 }
                >
                  <CollapsibleSectionList
                    sections={ column }
                    defaultExpandedKeys={ columnExpandedKeys }
                  />
                </GridItem>
              );
            }) }
          </Grid>
        );
      }) }
    </Flex>
  );
};

export default SectionLayoutDesktop;
